import React, { useCallback, useMemo } from 'react';
import { Typography, Grid } from '@material-ui/core';
import { Button } from '@clef/client-library';

import { useAppDispatch } from '../../../../../store';
import {
  addSegmentationMaskFile,
  deleteSegmentationMaskFile,
} from '../../../../../store/uploadState';
import MaskUploadIcon from '../../../../../images/segmentation_media_upload/mask_upload.png';
import ImageIcon from '../../../../../images/segmentation_media_upload/image.png';
import FolderIcon from '../../../../../images/segmentation_media_upload/folder.png';
import useUploadFileBlockQueue, {
  UploadFileBlock,
} from '../../../../../hooks/useUploadFileBlockQueue';
import { useNewUploadStyles } from '../../styles';
import { BaseDropFileZone } from './BaseDropFileZone';
import Markdown from 'markdown-to-jsx';

const HelpContent = `A Segmentation Mask tells LandingLens where the labeled areas are on an image, and what the Class is for each labeled area. 

The Segmentation Mask must be an 8-bit, single-channel PNG of the original image. A channel value must be assigned to each pixel. If a pixel doesn’t have a Class (if it’s not in a labeled area), the channel value must be 0. Use a specific non-zero value to indicate each Class.

You will later upload a Defect Map, which defines the Class for each channel value.
`;

export interface SegmentationUploadMaskDropFileZoneProps {
  fileCapacity?: number | null;
  fileLimit?: number | null;
  disabled?: boolean;
}

export const SegmentationUploadMaskDropFileZone: React.FC<
  SegmentationUploadMaskDropFileZoneProps
> = ({ fileCapacity, disabled, fileLimit }) => {
  const styles = useNewUploadStyles();
  const dispatch = useAppDispatch();
  const { maskBlocks } = useUploadFileBlockQueue();

  const getChipLabel = useCallback(
    (block: UploadFileBlock) => {
      return block.folderName ? (
        <Grid container alignItems="center" justifyContent="center">
          <img src={FolderIcon} className={styles.segmentationDropZoneChipIcon} />
          <span>{block.folderName}</span>
        </Grid>
      ) : (
        <Grid container alignItems="center" justifyContent="center">
          <img src={ImageIcon} className={styles.segmentationDropZoneChipIcon} />
          <span>
            {block.files.length === 1
              ? t('1 mask')
              : t('{{count}} masks', { count: block.files.length })}
          </span>
        </Grid>
      );
    },
    [styles.segmentationDropZoneChipIcon],
  );

  const helpContent = useMemo(() => <Markdown>{HelpContent}</Markdown>, []);

  return (
    <BaseDropFileZone
      blocks={maskBlocks}
      title={t('Segmentation mask')}
      acceptFileTypesText={t('PNG')}
      imageSrc={MaskUploadIcon}
      guideMessage={{
        mini: (
          <Typography className={styles.dropzoneText}>
            {t('Drag and drop folders/images, or ')}
            <strong className={styles.chooseFileText}>{t('Choose File')}</strong>
          </Typography>
        ),
        full: (
          <>
            <Typography className={styles.dropzoneText}>
              {t('Drag and drop folders/images, or')}
            </Typography>
            <Typography>
              <Button
                id="choose-seg-mask-file"
                variant="contained"
                color="primary"
                className={styles.chooseFileButton}
                disabled={disabled}
              >
                {t('Choose File')}
              </Button>
            </Typography>
          </>
        ),
      }}
      onDrop={files =>
        dispatch(addSegmentationMaskFile({ files, capacity: fileCapacity, limit: fileLimit }))
      }
      onDelete={files => {
        dispatch(deleteSegmentationMaskFile(files));
      }}
      getChipLabel={getChipLabel}
      acceptedFiles={['image/png']}
      acceptMultipleDrops={true}
      dropZoneDataTestId={'seg-mask-upload-input'}
      hasAcceptedFiles={!!maskBlocks.length}
      helpContent={helpContent}
      disabled={disabled}
    />
  );
};
